import { Div, Button, state, IRouteEvent } from '@codesuma/baseline'
import { SheetPage } from '../../components/sheet-page'
import catalog from '../../services/country-occasions'
import calendar from '../../services/calendar'
import { CatalogOccasion } from '../../services/occasion-catalog'
import { toFaDigits } from '../../helpers/digits'
import { Sources } from './sources'
import { OccasionRow } from './row'
import { RegionPicker } from './regions'
import { regionsWithExtras } from '../../services/occasion-groups'
import { inSelectedRegion, withoutReplaced } from '../../services/country-selection'
import style from './index.module.css'

// One country's occasions for the current Gregorian year, in date order, with a
// switch on every day.
//
// Route: /occasions/country/germany. The country is named in the path, not the
// query, because the page is about that country and the address should say so.
// The router caches a page per path, so each country gets an instance of its own
// rather than one page being redrawn — see app.ts.
//
// The nationwide list is what a country shows by default. A country with regions
// carries a picker above the list, and what is picked there decides which days
// the list carries — one row per occasion, whether one region keeps it or ten.

// The state the US page opens on, so its picker arrives with something in it
// instead of empty. Nothing else defaults: every other country with a picker
// opens on its nationwide list alone.
const OPENING: Record<string, string[]> = { US: ['US-CA'] }

export const CountryPage = () => {
    const base = SheetPage()
    // The wide gutter belongs on the reading edge only. On the left the list
    // runs out to the screen, so a row has room for a name and its translation
    // without either of them wrapping.
    // base.style({ paddingInlineEnd: '1rem' })

    const body = Div()
    body.addClass(style.body)
    base.append(body)

    // Which country is drawn. A back navigation re-enters with the same slug,
    // and redrawing then would throw away a list that is already correct.
    let shown = ''

    // The page chrome answers a settings change without anything being built
    // again — switching one day off would otherwise scroll the reader back to
    // the top of the year. Only a change to the picked regions rebuilds the
    // list, because then it is genuinely a different list.
    let syncers: (() => void)[] = []
    let rowSyncers: (() => void)[] = []
    const syncAll = () => [...syncers, ...rowSyncers].forEach(fn => fn())

    let refresh = syncAll

    const render = async (slug: string) => {
        body.empty()
        syncers = []
        rowSyncers = []
        refresh = syncAll
        const gy = calendar.today().gy

        // A country's occasions are not in the app — they are fetched the first
        // time it is opened and kept from then on, so this page can genuinely be
        // waiting on a network and has to say so.
        const loading = Div('در حال دریافت رویدادها…')
        loading.addClass(style.loading)
        body.append(loading)

        // The address names the country, so the directory has to arrive before
        // anything else can be asked for.
        const country = (await catalog.countries()).find(c => c.slug === slug)
        if (!country) {
            loading.remove()
            const missing = Div('این کشور پیدا نشد.')
            missing.addClass(style.empty)
            body.append(missing)
            return
        }
        const code = country.code
        base.setTitle(country.names.fa ?? country.names.en)

        const [definitions, occurrences] = await Promise.all([
            catalog.occasions(code),
            catalog.year(code, gy),
        ])
        loading.remove()
        const allIds = definitions.map(d => d.id)
        const byId = new Map<string, CatalogOccasion>(definitions.map(d => [d.id, d]))

        const subtitle = Div(`رویدادهای سال ${toFaDigits(gy)} میلادی`)
        subtitle.addClass(style.subtitle)
        body.append(subtitle)

        const regions = regionsWithExtras(country, definitions)

        // What the picker opens on, before the reader has chosen anything. It
        // belongs to the page and not to a subscription: nothing is in the
        // calendar yet, so there is nothing to keep it in, and dropping the pill
        // must not have to write one to record it. Absent means every state,
        // which is also how a country with no picker reads. The moment a day is
        // added there is a subscription, and it answers instead of this.
        let opening: string[] | undefined = regions.length ? OPENING[code] ?? [] : undefined
        const chosen = () => catalog.has(code) ? catalog.picked(code) : opening

        const selection = {
            all: () => chosen() === undefined,
            has: (region: string) => chosen()?.includes(region) ?? true,
            set: (region: string, on: boolean) => {
                if (catalog.has(code)) {
                    if (on) catalog.addRegion(code, region)
                    else catalog.removeRegion(code, region)
                    return
                }
                opening = on
                    ? opening && [...opening, region]
                    : (opening ?? regions).filter(r => r !== region)
                refresh()
            },
            setAll: (on: boolean) => {
                if (catalog.has(code)) {
                    if (on) catalog.addAllRegions(code)
                    else catalog.clearRegions(code)
                    return
                }
                opening = on ? undefined : []
                refresh()
            },
        }

        // The picker sits above the switch below it, because what is picked
        // decides what that switch is talking about.
        if (regions.length) body.append(RegionPicker(country, regions, syncers, selection))

        let listed: CatalogOccasion[] = []
        const allShowing = () => catalog.allShowing(code, listed)

        // Made here, put in last: it is stuck to the foot of the page, and a
        // sticky element holds to the bottom of the scroll for as long as the
        // content it comes after is still above it.
        const bar = Div()
        bar.addClass(style.bar)
        const addAll = Button()
        addAll.addClass(style.add)
        addAll.on('click', () =>
            catalog.setAllShown(code, listed.map(o => o.id), !allShowing(), chosen()))
        syncers.push(() => {
            const on = allShowing()
            addAll.el.textContent = on
                ? 'حذف رویدادهای اضافه شده از تقویم'
                : 'افزودن همهٔ رویدادها به تقویم'
            addAll.toggleClass(style.added, on)
        })
        bar.append(addAll)

        if (!occurrences.length) {
            const empty = Div('رویدادهای این کشور دریافت نشد. برای بار اول به اینترنت نیاز است.')
            empty.addClass(style.empty)
            body.append(empty)
            body.append(bar)
            syncAll()
            return
        }

        const list = Div()
        list.addClass(style.list)
        body.append(list)

        // Absent means every region, which no list of codes can stand for.
        const pickedKey = () => chosen()?.join(',') ?? '*'
        let key = ''

        const drawList = () => {
            key = pickedKey()
            const picked = chosen()
            // Exactly what the calendar will carry for these regions. A day that
            // another one stands in for — Columbus Day, where a region keeps
            // Indigenous Peoples' Day instead — is left out rather than listed
            // under a switch that could never do anything.
            const listable = new Set(withoutReplaced(definitions
                .filter(d => inSelectedRegion(d, picked, country.regions))).map(d => d.id))
            rowSyncers = []
            list.empty()
            const seen = new Map<string, CatalogOccasion>()
            for (const occurrence of occurrences) {
                const occasion = byId.get(occurrence.occasionId)
                if (!occasion || !listable.has(occasion.id)) continue
                seen.set(occasion.id, occasion)
                list.append(OccasionRow(
                    occasion, occurrence, country, allIds, byId, rowSyncers, picked))
            }
            listed = [...seen.values()]
        }

        refresh = () => {
            if (pickedKey() !== key) drawList()
            syncAll()
        }
        drawList()

        // The catalog is deliberately partial, and a list that hides that reads
        // as complete when it is not.
        if (country.note?.fa) {
            const footer = Div(country.note.fa)
            footer.addClass(style.footer)
            body.append(footer)
        }
        body.append(Sources(country, definitions))
        body.append(bar)

        syncAll()
    }

    base.on('enter', ({ params }: IRouteEvent) => {
        const slug = String((params as Record<string, string>)?.slug || '')
        if (!slug || slug === shown) return
        shown = slug
        void render(slug)
    })

    const onSettings = () => refresh()
    state.on('settings', onSettings)
    base.on('unmounted', () => state.off('settings', onSettings))

    return base
}
